/**
 * QuoteDraftEditor — an editable line-item draft on the deal detail page.
 *
 * This is the "hands" surface for the `crm_prefill_quote` frontend action: the Global
 * Agent fills the draft (product_name / quantity / unit_price / discount), the user
 * reviews + edits, then clicks Save. Saving REPLACES the deal's line items via the
 * existing updateDeal API (the confirmed human gate is the explicit Save click — the
 * agent never writes to the DB itself).
 */

import React, { useState, useRef, useCallback } from 'react';
import { Card, Button, toast } from '../../components/ui';
import { Sparkles, Plus, X, Save } from '../../lib/icons';
import { updateDeal } from '../../lib/crm-api';
import { usePageActions } from '../../hooks/usePageActions';
import { type DraftItem, lineTotal, normalizeQuoteItems } from './quote-draft-utils';

function emptyRow(): DraftItem {
  return { product_name: '', quantity: 1, unit_price: 0, discount_percent: 0 };
}

interface Props {
  dealId: number;
  currency: string;
  /** Called after a successful save so the parent can reload the deal. */
  onSaved: () => void;
}

export function QuoteDraftEditor({ dealId, currency, onSaved }: Props) {
  const [items, setItems] = useState<DraftItem[]>([]);
  const [prefilled, setPrefilled] = useState(false);
  const [saving, setSaving] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  // ── Frontend action: agent fills this draft for the user to review + save ──
  usePageActions(
    [
      {
        name: 'crm_prefill_quote',
        description:
          'Pre-fill a draft quote (line items) on the deal the user is currently viewing, for them to review and Save. ' +
          'Provide `items`, each with product_name, quantity, unit_price, and optional discount_percent (0-100). ' +
          'This does NOT write to the database — it only fills the editable on-screen draft; the user reviews and clicks Save. ' +
          'Use this when the user asks to draft / prepare / add a quote or line items on a deal.',
        safety: 'auto',
        parameters: {
          type: 'object',
          properties: {
            items: {
              type: 'array',
              description: 'Line items to pre-fill into the draft quote',
              items: {
                type: 'object',
                properties: {
                  product_name: { type: 'string', description: 'Product name' },
                  quantity: { type: 'number', description: 'Quantity, must be > 0' },
                  unit_price: { type: 'number', description: 'Unit price, must be >= 0' },
                  discount_percent: { type: 'number', description: 'Optional line discount percent, 0-100' },
                },
                required: ['product_name', 'quantity', 'unit_price'],
                additionalProperties: false,
              },
            },
          },
          required: ['items'],
          additionalProperties: false,
        },
        execute: (params) => {
          const normalized = normalizeQuoteItems((params as { items?: unknown }).items);
          if (normalized.length === 0) {
            return { ok: false, error: 'No valid line items provided (need product_name + quantity > 0).' };
          }
          setItems(normalized);
          setPrefilled(true);
          setTimeout(() => cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
          const subtotal = normalized.reduce((s, i) => s + lineTotal(i), 0);
          return { ok: true, count: normalized.length, subtotal, note: 'Draft filled — awaiting user review + Save.' };
        },
      },
    ],
    [],
  );

  const patch = useCallback((idx: number, field: keyof DraftItem, value: string) => {
    setItems((prev) =>
      prev.map((it, i) => (i === idx ? { ...it, [field]: field === 'product_name' ? value : Number(value) || 0 } : it)),
    );
  }, []);

  const removeRow = useCallback((idx: number) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const addRow = useCallback(() => setItems((prev) => [...prev, emptyRow()]), []);

  const clear = useCallback(() => {
    setItems([]);
    setPrefilled(false);
  }, []);

  const save = useCallback(async () => {
    const valid = items.filter((it) => it.product_name.trim() && it.quantity > 0);
    if (valid.length === 0) {
      toast('Add at least one line item with a name and quantity.', 'error');
      return;
    }
    setSaving(true);
    try {
      await updateDeal(dealId, {
        items: valid.map((it, i) => ({
          product_name: it.product_name.trim(),
          quantity: it.quantity,
          unit_price: it.unit_price,
          discount_percent: it.discount_percent,
          line_total: lineTotal(it),
          sort_order: i,
        })),
      });
      toast('Line items saved to deal', 'success');
      clear();
      onSaved();
    } catch {
      toast('Failed to save line items', 'error');
    } finally {
      setSaving(false);
    }
  }, [items, dealId, clear, onSaved]);

  const subtotal = items.reduce((s, i) => s + lineTotal(i), 0);
  const cur = currency === 'USD' ? '$' : '';

  return (
    <div ref={cardRef}>
      <Card className={`p-4 transition-colors ${prefilled ? 'border-primary-edge bg-primary-subtle/20' : ''}`}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-semibold text-fg-muted uppercase tracking-wide flex items-center gap-1.5">
            <Sparkles size={12} className="text-primary-fg" />
            Quote Draft
            {prefilled && (
              <span className="text-[10px] font-normal text-primary-fg normal-case">· prefilled by agent</span>
            )}
          </h3>
          {items.length > 0 && (
            <button onClick={clear} className="text-[11px] text-fg-faint hover:text-fg-secondary transition-colors">
              Clear
            </button>
          )}
        </div>

        {items.length === 0 ? (
          <div className="text-center py-5">
            <p className="text-sm text-fg-faint">
              Ask the Global Agent to draft a quote (e.g. “add 10× LetPot Max at $99 and 5× nutrient at $20”), or add a
              line manually.
            </p>
            <Button size="sm" variant="ghost" className="mt-3" onClick={addRow}>
              <Plus size={14} className="mr-1" /> Add line
            </Button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[560px]">
                <thead>
                  <tr className="border-b border-edge text-left">
                    <th className="text-xs font-medium text-fg-muted py-2 w-8">#</th>
                    <th className="text-xs font-medium text-fg-muted py-2">Product</th>
                    <th className="text-xs font-medium text-fg-muted py-2 text-right w-20">Qty</th>
                    <th className="text-xs font-medium text-fg-muted py-2 text-right w-28">Unit Price</th>
                    <th className="text-xs font-medium text-fg-muted py-2 text-right w-20">Disc %</th>
                    <th className="text-xs font-medium text-fg-muted py-2 text-right w-28">Total</th>
                    <th className="w-8" />
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, idx) => (
                    <tr key={idx} className="border-b border-edge last:border-b-0">
                      <td className="py-1.5 text-xs text-fg-muted">{idx + 1}</td>
                      <td className="py-1.5 pr-2">
                        <input
                          value={it.product_name}
                          onChange={(e) => patch(idx, 'product_name', e.target.value)}
                          placeholder="Product name"
                          className="w-full bg-surface-sunken border border-edge-strong rounded px-2 py-1 text-sm text-fg focus:outline-none focus:ring-2 focus:ring-primary-500/40"
                        />
                      </td>
                      <td className="py-1.5">
                        <input
                          type="number"
                          value={it.quantity}
                          min={0}
                          onChange={(e) => patch(idx, 'quantity', e.target.value)}
                          className="w-full bg-surface-sunken border border-edge-strong rounded px-2 py-1 text-sm text-fg text-right focus:outline-none focus:ring-2 focus:ring-primary-500/40"
                        />
                      </td>
                      <td className="py-1.5 pl-1">
                        <input
                          type="number"
                          value={it.unit_price}
                          min={0}
                          step="0.01"
                          onChange={(e) => patch(idx, 'unit_price', e.target.value)}
                          className="w-full bg-surface-sunken border border-edge-strong rounded px-2 py-1 text-sm text-fg text-right focus:outline-none focus:ring-2 focus:ring-primary-500/40"
                        />
                      </td>
                      <td className="py-1.5 pl-1">
                        <input
                          type="number"
                          value={it.discount_percent}
                          min={0}
                          max={100}
                          onChange={(e) => patch(idx, 'discount_percent', e.target.value)}
                          className="w-full bg-surface-sunken border border-edge-strong rounded px-2 py-1 text-sm text-fg text-right focus:outline-none focus:ring-2 focus:ring-primary-500/40"
                        />
                      </td>
                      <td className="py-1.5 text-sm text-fg text-right font-medium">
                        {cur}
                        {lineTotal(it).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                      </td>
                      <td className="py-1.5 text-center">
                        <button
                          onClick={() => removeRow(idx)}
                          className="text-fg-faint hover:text-destructive transition-colors"
                          title="Remove line"
                        >
                          <X size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-edge">
                    <td colSpan={5} className="py-2 text-right text-xs font-medium text-fg-muted">
                      Subtotal
                    </td>
                    <td className="py-2 text-right text-sm font-bold text-fg">
                      {cur}
                      {subtotal.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="flex items-center justify-between mt-3">
              <Button size="sm" variant="ghost" onClick={addRow}>
                <Plus size={14} className="mr-1" /> Add line
              </Button>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-fg-faint">Saving replaces the deal’s line items.</span>
                <Button size="sm" variant="default" onClick={save} disabled={saving}>
                  <Save size={14} className="mr-1" />
                  {saving ? 'Saving…' : 'Save to deal'}
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
